import { useState } from 'react'
import {
  type LayoutChangeEvent,
  type ViewProps,
  type ViewStyle,
  useWindowDimensions,
} from 'react-native'
import { Button } from '~/components/button'
import { useTheme, useAutoPosition } from '~/hooks/index'
import { useTooltipConstants } from '../../constants'
import { useLayout } from '../../hooks/useLayout'
import type { LayoutType, TTooltipAlign, TTooltipSide } from '../../types'
import { PositionAlignment, PositionDirection } from '../../types/enums'
import { TooltipArrow } from './components/tooltip-arrow'
import * as Comp from './tooltip-content.styles'

interface TTooltipContentProps extends ViewProps {
  side?: TTooltipSide
  align?: TTooltipAlign
  isOpen?: boolean
  content?: string
  isCloseable?: boolean
  autoPosition?: boolean
  triggerLayout?: LayoutType
  onClose?: () => void
}

export function TooltipContent({
  align = PositionAlignment.MIDDLE,
  side = PositionDirection.BOTTOM,
  isOpen = false,
  content = '',
  isCloseable = true,
  autoPosition = true,
  triggerLayout = null,
  onClose,
  ...rest
}: TTooltipContentProps) {
  const theme = useTheme()
  const tooltipConstants = useTooltipConstants()
  const layoutUtils = useLayout()
  const { width: screenWidth } = useWindowDimensions()
  const [contentLayout, setContentLayout] = useState<LayoutType>(null)

  const WRAPPER_HEIGHT =
    (triggerLayout?.height || 0) + (contentLayout?.height || 0)

  const sideString = side.toString() as 'top' | 'bottom' | 'left' | 'right'
  const alignString = align.toString() as 'start' | 'middle' | 'end'

  const optimalPosition = useAutoPosition({
    triggerLayout,
    contentLayout,
    preferredSide: sideString,
    preferredAlign: alignString,
    gap: tooltipConstants.SCREEN_MARGIN,
    screenPadding: tooltipConstants.SCREEN_PADDING,
    fixedLateralWidth: tooltipConstants.FIXED_LATERAL_WIDTH,
    autoPosition,
  })

  const finalSide = autoPosition
    ? PositionDirection[
        optimalPosition.side.toUpperCase() as keyof typeof PositionDirection
      ]
    : side
  const finalAlign = autoPosition
    ? PositionAlignment[
        optimalPosition.align.toUpperCase() as keyof typeof PositionAlignment
      ]
    : align

  if (!isOpen) return null

  const handleLayout = (event: LayoutChangeEvent) => {
    const { x, y, width, height } = event.nativeEvent.layout
    const newLayout = { x, y, width, height }

    if (layoutUtils.hasLayoutChanged(contentLayout, newLayout)) {
      setContentLayout(newLayout)
    }
  }

  const sideWrapperStyles = layoutUtils.getSideWrapperStyles(
    finalSide,
    triggerLayout,
    contentLayout
  )

  const alignContentStyles: ViewStyle = layoutUtils.calculateContentMargins(
    finalSide,
    finalAlign,
    triggerLayout,
    contentLayout,
    screenWidth,
    WRAPPER_HEIGHT
  )

  const themeMaxWidth =
    theme.sizingEnormous * theme.sizingNano + theme.sizingMediumlarge
  const maxWidth = layoutUtils.getMaxWidth(
    finalSide,
    triggerLayout,
    screenWidth,
    themeMaxWidth
  )

  return (
    <Comp.Wrapper
      style={{
        left: -(triggerLayout?.x || 0),
        height: WRAPPER_HEIGHT,
        width: screenWidth,
        ...sideWrapperStyles,
      }}
      align={finalAlign}
      side={finalSide}
      onPress={onClose}
    >
      <TooltipArrow
        side={finalSide}
        triggerLayout={triggerLayout}
        contentLayout={contentLayout}
      />
      <Comp.Container
        {...rest}
        style={[{ maxWidth }, alignContentStyles]}
        onLayout={handleLayout}
      >
        <Comp.Content
          numberOfLines={tooltipConstants.MAX_LINES}
          ellipsizeMode="tail"
        >
          {content}
        </Comp.Content>
        {isCloseable && onClose && (
          <Button.Root
            testID="tooltip-close-button"
            onPress={onClose}
            variant="neutral-tertiary"
            size="ml"
          >
            <Button.Icon symbol="rdicon-dismiss" />
          </Button.Root>
        )}
      </Comp.Container>
    </Comp.Wrapper>
  )
}
